import { parseMoneyToCents, tt, type MoneyInput } from '@agos/shared'

/**
 * The closure record, and what one can actually hold.
 *
 * `fiskal_closures` has columns for a closure number, a receipt count and a VAT
 * breakdown. **None of them is ever written.** `FiskalClosureService.record` sets
 * only `kasaId`, `type`, `result`, `revenue` and `rawResponse`, and all 100 rows in
 * the restored dumps have the other three `null`.
 *
 * That is not a display problem. The transport does not read the register's
 * counters back — a Z-report's `rawResponse` is `{"raw":"ACK"}`, which is the device
 * saying it did the thing, not what the thing contained. So legacy's history table
 * renders four columns that are structurally always `—`, and the fifth is worse:
 *
 * **`revenue` is not revenue.** It is set only for a deposit or a withdrawal, where
 * it holds the cash moved. On an X- or Z-report row — the rows that are *about*
 * revenue — it is always null. Legacy's column header says *Tržby*.
 *
 * So the money column is named for what it contains, the empty columns are dropped,
 * and the page says once why the report rows carry no figures rather than printing
 * a dash four times per row. FB-073, with the backend half filed in
 * `MIGRATION-STATUS.md`.
 */

export type ClosureType = 'x-report' | 'z-report' | 'deposit' | 'withdraw'

export interface ClosureRecord {
  id?: number
  kasaId?: string
  type?: string
  result?: string
  /** Never written by the backend. Kept because the column exists. */
  closureNumber?: string | null
  /** The cash moved by a deposit or withdrawal. `null` on a report. */
  revenue?: MoneyInput
  /** Never written by the backend. */
  receiptCount?: number | string | null
  /** Never written by the backend. */
  vatBreakdown?: Record<string, number | string> | null
  /**
   * What the device answered. For a report, `{"raw":"ACK"}`.
   *
   * **Still on the payload and no longer on the screen - §9.414.** The owner: *"who made it, instead
   * of ODPOVEĎ KASY because there is ACK ACK ACK ... which is not important."* Measured on their
   * data: **every** `z-report`'s reply is those three characters, so the column the code called *"the
   * only column with real content on a report row"* was the emptiest one for the rows that matter.
   * Kept in the type and in the database, because an `ACK` that ever stops being an `ACK` is worth
   * having.
   */
  rawResponse?: Record<string, unknown> | null
  /** Who pressed it, by name - §10A.189. Null on anything recorded before `V57`. */
  performedBy?: string | null
  /** What they wrote against it - §10A.189. Collected before `V57` and discarded. */
  note?: string | null
  /**
   * What AGOS believed the register had taken, at the moment of this Z - §10A.190, `V58`.
   *
   * **Nothing on this screen draws these yet, and that is a decision.** Putting a second money
   * figure beside a tax document needs wording for what each number is and an answer for what to
   * show when the two disagree by a cent - a receipt printed and then stornoed being the obvious
   * case. That is FB-731's banked half. Declared here so the display is a JSX change when the
   * decision is made, and so the payload's shape is written down where the table reads it.
   *
   * Null on anything but a successful `z-report`, and on every closure recorded before `V58`.
   */
  agosTakingsFrom?: string | null
  agosTakingsCash?: number | string | null
  agosTakingsCard?: number | string | null
  agosTakingsTotal?: number | string | null
  createdAt?: string
}

/**
 * Who performed a closure, as the table shows it - §9.414.
 *
 * **An absent author is said, not blanked.** `V57` back-fills nothing, so every closure recorded
 * before it has no name - and a blank cell reads as *nobody did it* rather than as *nobody wrote it
 * down*. The same distinction §9.410 needed for a missing Z-report and §9.413 for a missing
 * handover: three screens, one mistake, which is why it is a named helper rather than a `??` in the
 * JSX.
 */
export function performedByLabel(record: ClosureRecord): string {
  const name = (record.performedBy ?? '').trim()
  return name === '' ? tt('nezaznamenané') : name
}

const TYPE_LABELS: Record<ClosureType, string> = {
  'x-report': tt('Prehľadová uzávierka'),
  'z-report': tt('Denná uzávierka'),
  deposit: tt('Vklad'),
  withdraw: tt('Výber'),
}

export function typeLabel(type: string | undefined): string {
  if (!type) return ''
  return TYPE_LABELS[type as ClosureType] ?? type
}

export type BadgeColor = 'red' | 'blue' | 'emerald' | 'orange' | 'zinc' | 'amber' | 'violet'

const TYPE_COLORS: Record<ClosureType, BadgeColor> = {
  'z-report': 'red',
  'x-report': 'blue',
  deposit: 'emerald',
  withdraw: 'orange',
}

export function typeColor(type: string | undefined): BadgeColor {
  return TYPE_COLORS[type as ClosureType] ?? 'zinc'
}

const KASA_LABELS: Record<string, string> = {
  restauracia: tt('Reštaurácia'),
  rozvoz: tt('Rozvoz'),
}

export function kasaLabel(kasaId: string | undefined): string {
  if (!kasaId) return ''
  return KASA_LABELS[kasaId] ?? kasaId
}

export function kasaColor(kasaId: string | undefined): BadgeColor {
  if (kasaId === 'restauracia') return 'amber'
  if (kasaId === 'rozvoz') return 'violet'
  return 'zinc'
}

/** Whether this record moved cash in or out of the drawer, rather than reporting. */
export function movesCash(type: string | undefined): boolean {
  return type === 'deposit' || type === 'withdraw'
}

/**
 * The cash a row moved, signed — `null` for a report, which moved none.
 *
 * A withdrawal is stored as a positive number with `type: 'withdraw'`, so the sign
 * comes from the type. A column that showed both as positive would make an evening
 * of deposits and withdrawals unreadable.
 */
export function movedCents(record: ClosureRecord): number | null {
  if (!movesCash(record.type)) return null
  if (record.revenue === null || record.revenue === undefined) return null
  const cents = parseMoneyToCents(record.revenue)
  return record.type === 'withdraw' ? -cents : cents
}

/** What the register answered, flattened for display. */
export function deviceAnswer(record: ClosureRecord): string {
  const response = record.rawResponse
  if (!response) return ''
  const raw = response.raw
  if (typeof raw === 'string') return raw
  const error = response.error
  if (typeof error === 'string') return error
  return Object.entries(response)
    .map(([key, value]) => `${key}: ${stringifyValue(value)}`)
    .join(' · ')
}

function stringifyValue(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return JSON.stringify(value)
}

export function failed(record: ClosureRecord): boolean {
  return record.result !== 'ok'
}

export type KasaFilter = 'all' | 'restauracia' | 'rozvoz'

export interface HistoryFilter {
  kasa: KasaFilter
  search: string
  from: string
  to: string
}

export function matchesSearch(record: ClosureRecord, search: string): boolean {
  const needle = search.trim().toLowerCase()
  if (!needle) return true
  return [
    typeLabel(record.type),
    kasaLabel(record.kasaId),
    record.closureNumber ?? '',
    // §9.414 - the two columns that replaced the register's answer. A search that cannot find what
    // the table shows is a search for the previous version of the screen.
    record.performedBy ?? '',
    record.note ?? '',
    // **Kept although its column is gone.** The answer is still stored, so somebody who types a
    // device code can still find the row - and dropping it would leave `deviceAnswer` with no
    // caller, which is how a helper becomes dead code nobody notices. Searching `ACK` matches every
    // report row, which is a deliberate act rather than an accident.
    deviceAnswer(record),
  ].some((field) => field.toLowerCase().includes(needle))
}

/**
 * The history, narrowed.
 *
 * The date bounds are inclusive at both ends: `to` covers the whole day, because a
 * closure filed at 20:18 on the last day of a month belongs to that month.
 */
export function filterHistory(
  records: ClosureRecord[],
  { kasa, search, from, to }: HistoryFilter,
): ClosureRecord[] {
  const after = from ? new Date(`${from}T00:00:00`).getTime() : null
  const before = to ? new Date(`${to}T23:59:59.999`).getTime() : null

  return records.filter((record) => {
    if (kasa !== 'all' && record.kasaId !== kasa) return false
    if (after !== null || before !== null) {
      const filedAt = record.createdAt ? new Date(record.createdAt).getTime() : Number.NaN
      if (!Number.isFinite(filedAt)) return false
      if (after !== null && filedAt < after) return false
      if (before !== null && filedAt > before) return false
    }
    return matchesSearch(record, search)
  })
}

/** The newest closure of a kind on a register — the status bar's "last Z-report". */
export function latestOfType(
  records: ClosureRecord[],
  type: ClosureType,
  kasaId: string,
): ClosureRecord | null {
  return (
    records
      .filter((record) => record.type === type && record.kasaId === kasaId)
      .sort(
        (left, right) =>
          new Date(right.createdAt ?? 0).getTime() - new Date(left.createdAt ?? 0).getTime(),
      )
      .at(0) ?? null
  )
}

export interface HistoryTotals {
  count: number
  depositedCents: number
  withdrawnCents: number
  /** Deposits minus withdrawals over the period shown. */
  netCents: number
  zReports: number
  errors: number
}

export function historyTotals(records: ClosureRecord[]): HistoryTotals {
  let depositedCents = 0
  let withdrawnCents = 0

  for (const record of records) {
    const cents = movedCents(record)
    if (cents === null) continue
    if (cents >= 0) depositedCents += cents
    else withdrawnCents += -cents
  }

  return {
    count: records.length,
    depositedCents,
    withdrawnCents,
    netCents: depositedCents - withdrawnCents,
    zReports: records.filter((record) => record.type === 'z-report').length,
    errors: records.filter(failed).length,
  }
}
